import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import { Bell, Building2, ChevronDown, Menu, Moon, RefreshCw, SlidersHorizontal, Sun } from 'lucide-react'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useBUContext } from '../../contexts/BUContext'
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useBusinessUnits } from '../../hooks/useBusinessUnits'
import { useTenant } from '../../hooks/useTenant'

const PAGE_LABELS: Record<string, string> = {
  '/':        'Executive Cockpit',
  '/revenue': 'Revenue Operations',
  '/margin':  'Margin Control',
  '/billing': 'Billing & Crediti',
  '/recurring-revenue': 'Recurring Revenue',
  '/upload':  'Carica file',
  '/sources': 'Sorgenti dati',
  '/orders':  'Commesse',
  '/ai':      'AI Chat',
  '/export':  'Esporta',
}

// Pagine dashboard che supportano i filtri globali KPI
const FILTER_PAGES = new Set(['/', '/revenue', '/margin', '/billing', '/recurring-revenue'])

export interface TopbarProps {
  onMenuClick: () => void
  onFilterClick: () => void
  filterActive: boolean
}

export function Topbar({ onMenuClick, onFilterClick, filterActive }: TopbarProps) {
  const { pathname } = useLocation()
  const { isDark, toggleTheme } = useTheme()
  const { hasActiveFilters } = useGlobalFilters()
  const { selectedBuId, setSelectedBuId } = useBUContext()
  const { data: tenant } = useTenant()
  const { data: businessUnits } = useBusinessUnits()
  const pageLabel = PAGE_LABELS[pathname] ?? 'Dashboard'
  const today = format(new Date(), "EEEE d MMM yyyy", { locale: it })
  const showFilterButton = FILTER_PAGES.has(pathname)
  const [buOpen, setBuOpen] = useState(false)

  const isEnterprise = tenant?.plan === 'enterprise'
  const activeBUs = (businessUnits || []).filter((bu) => bu.is_active && !bu.deleted_at)
  const showBuSwitcher = isEnterprise && activeBUs.length > 0 && FILTER_PAGES.has(pathname)
  const selectedBu = activeBUs.find((bu) => bu.id === selectedBuId)

  return (
    <header className="glass sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 px-4 md:px-6 relative">
      {/* Mobile hamburger */}
      <button
        onClick={onMenuClick}
        className="lg:hidden text-[--km-text-muted] hover:text-[--km-text-primary] p-1.5 rounded-lg hover:bg-white/[0.05] transition-colors"
        aria-label="Apri menu di navigazione"
      >
        <Menu size={20} />
      </button>

      {/* Page breadcrumb */}
      <div className="flex-1 flex items-center gap-2">
        <span className="text-[--km-text-muted] text-xs hidden sm:block">Koremargin</span>
        <span className="text-[--km-text-muted] text-xs hidden sm:block">/</span>
        <h1 className="text-sm font-semibold text-[--km-text-primary]">{pageLabel}</h1>
      </div>

      {/* Right actions */}
      <span className="hidden md:block text-xs text-[--km-text-muted] tabular-nums capitalize">
        {today}
      </span>

      <div className="flex items-center gap-2">
        {/* BU switcher — Enterprise con BU configurate, solo su pagine dashboard */}
        {showBuSwitcher && (
          <div className="relative">
            <button
              onClick={() => setBuOpen((o) => !o)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-all ${
                selectedBuId
                  ? 'border-indigo-500/50 text-indigo-400 bg-indigo-500/10'
                  : 'border-white/10 text-[--km-text-muted] hover:text-[--km-text-primary] hover:border-white/20'
              }`}
              aria-label="Seleziona Business Unit"
            >
              <Building2 size={12} />
              <span className="hidden sm:inline max-w-[100px] truncate">
                {selectedBu ? selectedBu.name : 'Tutte le BU'}
              </span>
              <ChevronDown size={10} className={`transition-transform ${buOpen ? 'rotate-180' : ''}`} />
            </button>
            {buOpen && (
              <div className="absolute right-0 mt-1 w-48 glass rounded-xl border border-white/10 shadow-xl z-50 py-1">
                <button
                  onClick={() => { setSelectedBuId(null); setBuOpen(false) }}
                  className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                    !selectedBuId ? 'text-indigo-400 bg-indigo-500/10' : 'text-[--km-text-muted] hover:text-[--km-text-primary] hover:bg-white/5'
                  }`}
                >
                  Tutte le BU
                </button>
                {activeBUs.map((bu) => (
                  <button
                    key={bu.id}
                    onClick={() => { setSelectedBuId(bu.id); setBuOpen(false) }}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center gap-2 ${
                      selectedBuId === bu.id ? 'text-indigo-400 bg-indigo-500/10' : 'text-[--km-text-muted] hover:text-[--km-text-primary] hover:bg-white/5'
                    }`}
                  >
                    <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: bu.color }} />
                    <span className="truncate">{bu.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

      <div className="flex items-center gap-1">
        {/* Filtri globali — solo sulle pagine dashboard */}
        {showFilterButton && (
          <button
            onClick={onFilterClick}
            className={`relative p-1.5 rounded-lg transition-all duration-150 ${
              filterActive || hasActiveFilters
                ? 'text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20'
                : 'text-[--km-text-muted] hover:text-[--km-text-primary] hover:bg-white/[0.05]'
            }`}
            aria-label="Filtri KPI"
            title="Filtri KPI"
          >
            <SlidersHorizontal size={14} />
            {/* Badge filtri attivi */}
            {hasActiveFilters && (
              <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-indigo-400" />
            )}
          </button>
        )}

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="text-[--km-text-muted] hover:text-[--km-text-primary] p-1.5 rounded-lg hover:bg-white/[0.05] transition-all duration-150"
          aria-label={isDark ? 'Passa a tema chiaro' : 'Passa a tema scuro'}
          title={isDark ? 'Tema chiaro' : 'Tema scuro'}
        >
          {isDark ? <Sun size={14} /> : <Moon size={14} />}
        </button>

        <button
          className="text-[--km-text-muted] hover:text-[--km-text-primary] p-1.5 rounded-lg hover:bg-white/[0.05] transition-all duration-150"
          aria-label="Aggiorna dati"
          title="Aggiorna dati"
        >
          <RefreshCw size={14} />
        </button>

        <button
          className="relative text-[--km-text-muted] hover:text-[--km-text-primary] p-1.5 rounded-lg hover:bg-white/[0.05] transition-all duration-150"
          aria-label="Notifiche"
          title="Notifiche"
        >
          <Bell size={14} />
          <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-indigo-500" />
        </button>
      </div>
      </div>

      {/* Bottom gradient accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent" />
    </header>
  )
}
